import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useCourts } from '../../../hooks/court/useCourts'
import { useBlocks } from '../../../hooks/block/useBlocks'

import Icon from '../../../components/ui/Icon'
import Loading from '../../../components/loading/Loading'
import { Button } from '../../../components/buttons/Button'
import PageHeader from '../../../components/ui/pageHeader/PageHeader'
import { BlockList } from '../../../components/authenticated/management/block/blockList/BlockList'
import { BlockCalendar } from '../../../components/authenticated/management/block/blockCalendar/BlockCalendar'

import { useApp } from '../../../contexts/AppContext'
import { useConfig } from '../../../contexts/ConfigContext'
import { BannerKind } from '../../../types/contexts/AppContext'
import { BlockModel, CourtModel } from '../../../types/court/court'
import { displayDate, getDateRangeFromToday } from '../../../utils/dateUtils'

const BLOCKS_PATH = '/m/blocks'

export const ManagerBlocks = () => {
  const navigate = useNavigate()
  const { showBanner } = useApp()
  const { t } = useTranslation('blocks')
  const { config, loading: configLoading } = useConfig()
  const [blockList, setBlockList] = useState<BlockModel[]>([])
  const [courtsList, setCourtsList] = useState<CourtModel[]>([])
  const [isReady, setIsReady] = useState(false)
  const [listRefresh, setListRefresh] = useState(false)

  const {
    courts,
    loading: courtsLoading,
    error: courtsError,
    resetState: resetCourtsState,
  } = useCourts()

  const {
    blocks,
    loading: blocksLoading,
    error: blocksError,
    resetState: resetBlocksState,
  } = useBlocks()

  const calendarDaysCount = config?.bookingWindowDays ?? 0
  const calendarRange = useMemo(
    () =>
      calendarDaysCount > 0 ? getDateRangeFromToday(calendarDaysCount) : null,
    [calendarDaysCount]
  )

  const loading = configLoading || courtsLoading || blocksLoading

  const refreshBlocksPageData = async () => {
    if (!calendarRange) {
      return
    }

    const courtsResponse = await courts()

    if (courtsResponse) {
      const courtList = courtsResponse.content ?? []
      setCourtsList(courtList)

      const courtsWithId = courtList.filter(
        (court): court is CourtModel & { id: number } => court.id !== undefined
      )
      const blocksResponses = await Promise.all(
        courtsWithId.map(async court => ({
          court,
          blocks: await blocks(
            court.id,
            calendarRange.startDate,
            calendarRange.endDate
          ),
        }))
      )

      setBlockList(
        blocksResponses
          .flatMap(({ court, blocks: courtBlocks }) =>
            courtBlocks.map(block => ({
              ...block,
              courtName: block.courtName ?? court.name,
            }))
          )
          .sort(
            (firstBlock, secondBlock) =>
              secondBlock.startTime.localeCompare(firstBlock.startTime) ||
              (secondBlock.id ?? Number.MIN_SAFE_INTEGER) -
                (firstBlock.id ?? Number.MIN_SAFE_INTEGER)
          )
      )
    }
  }

  useEffect(() => {
    if (!calendarRange) {
      return
    }

    const loadPageData = async () => {
      setIsReady(false)
      await refreshBlocksPageData()
      setIsReady(true)
    }

    void loadPageData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarRange])

  useEffect(() => {
    if (!courtsError && !blocksError) {
      return
    }

    showBanner({
      kind: BannerKind.ERR,
      msg: courtsError ?? blocksError!,
    })

    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courtsError, blocksError])

  const resetState = () => {
    resetCourtsState()
    resetBlocksState()
  }

  const goTo = (path: string) => {
    navigate(path)
  }

  const openNewBlock = (courtId: number, date?: string) => {
    const court = courtsList.find(court => court.id === courtId)

    if (!court) {
      return
    }

    const searchParams = new URLSearchParams({
      courtId: String(courtId),
    })

    if (date) {
      searchParams.set('startDate', date)
      searchParams.set('endDate', date)
    }

    goTo(`${BLOCKS_PATH}/new?${searchParams.toString()}`)
  }

  const openBlock = (courtId: number, date?: string, block?: BlockModel) => {
    if (block?.id !== undefined) {
      goTo(`${BLOCKS_PATH}/${block.id}/edit?courtId=${courtId}`)
      return
    }

    openNewBlock(courtId, date)
  }

  const handleAddBlock = () => {
    goTo(`${BLOCKS_PATH}/new`)
  }

  const handleCourtDayClick = (courtId: number, date: string) => {
    const block = blockList.find(block => {
      const startDate = displayDate(block.startTime)
      const endDate = displayDate(block.endTime)

      return block.courtId === courtId && date >= startDate && date <= endDate
    })

    openBlock(courtId, date, block)
  }

  const handleBlocksChanged = async () => {
    await refreshBlocksPageData()
    setListRefresh(value => !value)
  }

  return (
    <>
      <PageHeader
        title={{
          eyebrow: loading
            ? undefined
            : t('blocks.page-header.eyebrow', {
                courtsCount: courtsList.length,
                daysNumber: calendarDaysCount,
              }),
          h1: t('blocks.page-header.title'),
        }}
        sub={t('blocks.page-header.subtitle')}
        actions={[
          <Button
            key="add-block"
            label={t('blocks.page-header.new-btn')}
            icon={<Icon name="plus" size={14} />}
            size="md"
            loading={loading}
            disabled={courtsList[0]?.id === undefined}
            onClick={handleAddBlock}
          />,
        ]}
      />

      {!isReady ? (
        <Loading variant="card" />
      ) : (
        <div className="d-flex flex-column gap-4">
          {courtsList.length === 0 ? (
            <div className="ph-card">{t('blocks.no-courts')}</div>
          ) : (
            <BlockCalendar
              courts={courtsList}
              blocks={blockList}
              days={calendarRange?.days ?? []}
              onCellClick={handleCourtDayClick}
            />
          )}

          <BlockList refreshTrigger={listRefresh} onChanged={handleBlocksChanged} />
        </div>
      )}
    </>
  )
}
